import { useState, useRef } from 'react';
import { getConductores, searchConductoresBasic as searchConductoresBasicAPI } from '@shared/helpers/api/mantenedores/directorio';

/**
 * Hook para búsqueda de conductores con debounce y AbortController
 * Usado específicamente para dropdowns de selección de usuarioSpread
 */
export const useConductoresSearch = (user) => {
    const [conductores, setConductores] = useState([]);
    const [loadingConductores, setLoadingConductores] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    
    // Referencias para control de timeouts y abortar requests
    const searchTimeout = useRef(null);
    const abortController = useRef(null);

    const searchConductores = async (searchTerm) => {
        // Si el término de búsqueda es muy corto, limpiar resultados
        if (!searchTerm || searchTerm.length < 2) {
            setConductores([]);
            return;
        }

        // Abortar request anterior si existe
        if (abortController.current) {
            abortController.current.abort();
        }

        // Crear nuevo AbortController
        abortController.current = new AbortController();

        setLoadingConductores(true);
        try {
            const response = await getConductores(user, {
                searchValue: searchTerm,
                pageSize: 30,
                pageNumber: 1
            });

            // Solo actualizar si el request no fue abortado
            if (!abortController.current?.signal.aborted && response.success) {
                const conductoresOptions = response.data.map(conductor => ({
                    value: conductor.usuario_movil?.id || null,
                    label: conductor.usuario_movil?.usuarioSpread || `${conductor.nombre} ${conductor.apellido}`,
                    data: conductor
                })).filter(option => option.value !== null); // Solo incluir conductores con usuario_movil

                setConductores(conductoresOptions);
            }
        } catch (error) {
            // Solo mostrar error si no fue abortado
            if (!abortController.current?.signal.aborted) {
                console.error('Error al buscar conductores:', error);
                setConductores([]);
            }
        } finally {
            // Solo actualizar loading si no fue abortado
            if (!abortController.current?.signal.aborted) {
                setLoadingConductores(false);
            }
        }
    };

    const searchConductoresBasic = async (searchTerm) => {
        // Si el término de búsqueda es muy corto, limpiar resultados
        if (!searchTerm || searchTerm.length < 2) {
            setConductores([]);
            return;
        }

        // Abortar request anterior si existe
        if (abortController.current) {
            abortController.current.abort();
        }

        // Crear nuevo AbortController
        abortController.current = new AbortController();

        setLoadingConductores(true);
        try {
            const response = await searchConductoresBasicAPI(user, searchTerm);

            // Solo actualizar si el request no fue abortado
            if (!abortController.current?.signal.aborted && response.success) {
                const conductoresOptions = response.data.map(conductor => ({
                    value: conductor.id,
                    label: `${conductor.nombre} ${conductor.apellido}`,
                    data: conductor
                }));

                setConductores(conductoresOptions);
            }
        } catch (error) {
            // Solo mostrar error si no fue abortado
            if (!abortController.current?.signal.aborted) {
                console.error('Error al buscar conductores básicos:', error);
                setConductores([]);
            }
        } finally {
            // Solo actualizar loading si no fue abortado
            if (!abortController.current?.signal.aborted) {
                setLoadingConductores(false);
            }
        }
    };

    const handleSearchChange = (inputValue) => {
        setSearchValue(inputValue);

        // Limpiar timeout anterior
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        // Configurar nuevo timeout para debounce
        searchTimeout.current = setTimeout(() => {
            searchConductores(inputValue);
        }, 500);
    };

    const clearSearch = () => {
        setSearchValue('');
        setConductores([]);
        
        // Abortar request en curso si existe
        if (abortController.current) {
            abortController.current.abort();
        }
        
        // Limpiar timeout
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        
        setLoadingConductores(false);
    };

    const handleSearchChangeBasic = (inputValue) => {
        setSearchValue(inputValue);

        // Limpiar timeout anterior
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        // Configurar nuevo timeout para debounce
        searchTimeout.current = setTimeout(() => {
            searchConductoresBasic(inputValue);
        }, 500);
    };

    return {
        conductores,
        loadingConductores,
        searchValue,
        handleSearchChange,
        handleSearchChangeBasic,
        clearSearch
    };
};
